import { useState, useMemo } from "react";
import { useGetProfitLoss, useGetExpenseBreakdown, useGetSalesChart, getGetProfitLossQueryKey, useListSales, useGetTopProducts, useListReturns } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Loader2, Download, FileSpreadsheet, Crown, Calendar, ReceiptText, ShoppingBag, Receipt } from "lucide-react";
import { EnterpriseGate } from "@/components/enterprise-gate";
import { useAuth } from "@/context/auth";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { useBranch } from "@/context/branch";
import { readJson } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

type Period = "week" | "month" | "quarter" | "year";
type DailyCloseData = {
  date: string;
  revenue: number;
  transactions: number;
  totalDiscount: number;
  totalTax: number;
  returnVatSnapshotsComplete?: boolean;
  returnRevenueSnapshotsComplete?: boolean;
  totalExpenses: number;
  netProfit: number;
  byPaymentMethod: { method: string; total: number; count: number }[];
  topProducts: { productName: string; qty: number; revenue: number }[];
};

function formatCurrency(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

// ─── Daily Close Report ───────────────────────────────────────────────────────
function DailyCloseReport() {
  const { activeBranchId, activeBranch } = useBranch();
  const { data, isLoading } = useQuery<DailyCloseData>({
    queryKey: ["daily-close", activeBranchId],
    queryFn: async () => {
      const query = activeBranchId ? `?branchId=${activeBranchId}` : "";
      const res = await fetch(`${BASE}/api/dashboard/daily-close${query}`, { credentials: "include" });
      const result = await readJson<DailyCloseData>(res, "Could not load daily close report");
      if (!Array.isArray(result.byPaymentMethod) || !Array.isArray(result.topProducts)) {
        throw new Error("Unexpected daily close report response");
      }
      return result;
    },
  });

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Daily Close Report", 14, 20);
    doc.setFontSize(11);
    doc.text(`Date: ${format(new Date(data.date), "EEEE, MMMM d, yyyy")}`, 14, 30);
    autoTable(doc, {
      startY: 38,
      head: [["Metric", "Amount"]],
      body: [
        ["Total Revenue", formatCurrency(data.revenue)],
        ["Total Transactions", String(data.transactions)],
        ["Total Discounts Given", formatCurrency(data.totalDiscount)],
        ["Total Tax Collected", formatCurrency(data.totalTax)],
        ["Total Expenses", formatCurrency(data.totalExpenses)],
        ["Net Profit (Revenue − VAT − Expenses)", formatCurrency(data.netProfit)],
      ],
    });
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    if (data.byPaymentMethod.length) {
      autoTable(doc, {
        startY: finalY,
        head: [["Payment Method", "Transactions", "Amount"]],
        body: data.byPaymentMethod.map(r => [r.method.charAt(0).toUpperCase() + r.method.slice(1), String(r.count), formatCurrency(r.total)]),
      });
    }
    doc.save(`daily_close_${format(new Date(data.date), "yyyy-MM-dd")}.pdf`);
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!data) return null;

  const isProfit = data.netProfit >= 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Daily Close Report — {activeBranch?.name ?? "All Branches"}</h2>
          <p className="text-sm text-muted-foreground">{format(new Date(data.date), "EEEE, MMMM d, yyyy")}</p>
        </div>
        <Button variant="outline" onClick={exportPdf} className="gap-2">
          <Download className="w-4 h-4" /> Export PDF
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-1"><span className="text-sm text-muted-foreground">Today's Revenue</span><DollarSign className="w-4 h-4 text-emerald-500" /></div>
            <div className="text-2xl font-bold text-emerald-600">{formatCurrency(data.revenue)}</div>
            <p className="text-xs text-muted-foreground mt-0.5">{data.transactions} transaction{data.transactions !== 1 ? "s" : ""}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-1"><span className="text-sm text-muted-foreground">Expenses Today</span><Receipt className="w-4 h-4 text-blue-500" /></div>
            <div className="text-2xl font-bold">{formatCurrency(data.totalExpenses)}</div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${isProfit ? "border-l-primary" : "border-l-destructive"}`}>
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-1"><span className="text-sm text-muted-foreground">Net Profit</span>{isProfit ? <TrendingUp className="w-4 h-4 text-primary" /> : <TrendingDown className="w-4 h-4 text-destructive" />}</div>
            <div className={`text-2xl font-bold ${isProfit ? "text-primary" : "text-destructive"}`}>{formatCurrency(data.netProfit)}</div>
            <p className="text-xs text-muted-foreground mt-0.5">Revenue less collected VAT and expenses</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-1"><span className="text-sm text-muted-foreground">Discounts Given</span><ShoppingBag className="w-4 h-4 text-muted-foreground" /></div>
            <div className="text-xl font-bold">{formatCurrency(data.totalDiscount)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex justify-between items-start mb-1"><span className="text-sm text-muted-foreground">Tax Collected</span><ReceiptText className="w-4 h-4 text-muted-foreground" /></div>
            <div className="text-xl font-bold">{formatCurrency(data.totalTax)}</div>
          </CardContent>
        </Card>
      </div>

      {(data.returnVatSnapshotsComplete === false || data.returnRevenueSnapshotsComplete === false) && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Some earlier return revenue/tax adjustments cannot be fully verified because those transactions did not store per-item discount/VAT snapshots. New POS sales will retain the required details.
        </p>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {data.byPaymentMethod.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">By Payment Method</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.byPaymentMethod.map(r => (
                  <div key={r.method} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="capitalize font-medium text-sm">{r.method}</span>
                      <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">{r.count} tx</span>
                    </div>
                    <span className="font-bold">{formatCurrency(r.total)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {data.topProducts.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Top Products Today</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.topProducts.map((p, i) => (
                  <div key={p.productName} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">{i + 1}</span>
                      <span className="font-medium text-sm">{p.productName}</span>
                      <span className="text-xs text-muted-foreground">×{p.qty}</span>
                    </div>
                    <span className="font-bold text-sm">{formatCurrency(p.revenue)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Advanced Analytics ────────────────────────────────────────────────────

function AdvancedAnalytics() {
  const { activeBranchId } = useBranch();
  const branchParams = { branchId: activeBranchId ?? undefined };
  const { data: sales = [], isLoading: loadingSales } = useListSales(branchParams);
  const { data: topProducts = [], isLoading: loadingTop } = useGetTopProducts(branchParams);
  const { data: returns = [], isLoading: loadingReturns } = useListReturns();
  const branchSales = sales as any[];
  const branchSaleIds = new Set(branchSales.map(sale => sale.id));
  const revenueReturns = (returns as any[]).filter(r =>
    branchSaleIds.has(r.saleId) && r.status === "processed" && ["refund", "store_credit"].includes(r.type)
  );

  const hourlyData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, h) => ({ hour: h, sales: 0, revenue: 0 }));
    for (const s of sales as any[]) {
      const h = new Date(s.createdAt).getHours();
      hours[h].sales += 1;
      hours[h].revenue += Number(s.totalAmount ?? 0);
    }
    for (const r of revenueReturns) {
      const h = new Date(r.createdAt).getHours();
      hours[h].revenue -= Number(r.amount ?? 0);
    }
    return hours.map(h => ({
      label: h.hour === 0 ? "12am" : h.hour < 12 ? `${h.hour}am` : h.hour === 12 ? "12pm" : `${h.hour - 12}pm`,
      sales: h.sales,
      revenue: h.revenue,
    }));
  }, [sales, revenueReturns]);

  const paymentData = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of sales as any[]) {
      const m = s.paymentMethod || "cash";
      map[m] = (map[m] ?? 0) + Number(s.totalAmount ?? 0);
    }
    const saleById = new Map(branchSales.map(sale => [sale.id, sale]));
    for (const r of revenueReturns) {
      const method = saleById.get(r.saleId)?.paymentMethod || "cash";
      map[method] = (map[method] ?? 0) - Number(r.amount ?? 0);
    }
    return Object.entries(map).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [sales, revenueReturns]);

  const avgTxData = useMemo(() => {
    const byDate: Record<string, { total: number; count: number }> = {};
    for (const s of sales as any[]) {
      const d = s.createdAt.slice(0, 10);
      if (!byDate[d]) byDate[d] = { total: 0, count: 0 };
      byDate[d].total += Number(s.totalAmount ?? 0);
      byDate[d].count += 1;
    }
    for (const r of revenueReturns) {
      const d = r.createdAt.slice(0, 10);
      if (!byDate[d]) byDate[d] = { total: 0, count: 0 };
      byDate[d].total -= Number(r.amount ?? 0);
    }
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-30)
      .map(([date, { total, count }]) => ({
        date: date.slice(5),
        avg: count > 0 ? Math.round(total / count) : 0,
      }));
  }, [sales, revenueReturns]);

  const totalTransactions = (sales as any[]).length;
  const totalRevenue = branchSales.reduce((s, r) => s + Number(r.totalAmount ?? 0), 0)
    - revenueReturns.reduce((sum, r) => sum + Number(r.amount ?? 0), 0);
  const avgTxValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;
  const peakHour = [...hourlyData].sort((a, b) => b.sales - a.sales)[0];

  if (loadingSales || loadingTop || loadingReturns) {
    return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (totalTransactions === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShoppingBag className="w-12 h-12 text-muted-foreground/40 mb-4" />
        <p className="font-semibold text-lg">No sales data yet</p>
        <p className="text-sm text-muted-foreground mt-1">Start recording sales to see advanced analytics here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Total Transactions</p>
            <p className="text-2xl font-bold">{totalTransactions.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Total Revenue</p>
            <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Avg. Transaction Value</p>
            <p className="text-2xl font-bold">{formatCurrency(avgTxValue)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground mb-1">Peak Hour</p>
            <p className="text-2xl font-bold">{peakHour?.label ?? "—"}</p>
            <p className="text-xs text-muted-foreground">{peakHour?.sales} sales</p>
          </CardContent>
        </Card>
      </div>

      {/* Sales by hour */}
      <Card>
        <CardHeader>
          <CardTitle>Sales by Hour of Day</CardTitle>
          <p className="text-sm text-muted-foreground">Number of transactions per hour — reveals your busiest trading hours</p>
        </CardHeader>
        <CardContent>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" interval={1} />
                <YAxis fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                  formatter={(v: number, name: string) => [name === "revenue" ? formatCurrency(v) : v, name === "revenue" ? "Revenue" : "Transactions"]}
                />
                <Bar dataKey="sales" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} name="sales" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Payment method split */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Method Split</CardTitle>
            <p className="text-sm text-muted-foreground">Revenue share by payment type</p>
          </CardHeader>
          <CardContent>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={paymentData} cx="50%" cy="45%" outerRadius={80} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {paymentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                    formatter={(v: number) => [formatCurrency(v), ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mt-1">
              {paymentData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-1.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  {d.name} — {formatCurrency(d.value)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top products */}
        <Card>
          <CardHeader>
            <CardTitle>Top Products by Revenue</CardTitle>
            <p className="text-sm text-muted-foreground">Best-performing products across all time</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(topProducts as any[]).slice(0, 8).map((p: any, i: number) => {
                const maxRev = Number((topProducts as any[])[0]?.totalRevenue ?? 1);
                const pct = maxRev > 0 ? (Number(p.totalRevenue) / maxRev) * 100 : 0;
                return (
                  <div key={p.productId} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold flex-shrink-0">{i + 1}</span>
                        <span className="truncate max-w-[160px]">{p.productName}</span>
                      </span>
                      <span className="font-semibold flex-shrink-0">{formatCurrency(Number(p.totalRevenue))}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground">{p.totalSold} units sold</p>
                  </div>
                );
              })}
              {!(topProducts as any[]).length && (
                <p className="text-sm text-muted-foreground text-center py-8">No product sales data yet.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Avg transaction value trend */}
      {avgTxData.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Avg. Transaction Value — Last 30 Days</CardTitle>
            <p className="text-sm text-muted-foreground">How much each customer spends per visit over time</p>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={avgTxData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                  <YAxis fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `₦${(v / 1000).toFixed(0)}k`} />
                  <RechartsTooltip
                    contentStyle={{ backgroundColor: "hsl(var(--card))", borderColor: "hsl(var(--border))", borderRadius: "8px" }}
                    formatter={(v: number) => [formatCurrency(v), "Avg. transaction"]}
                  />
                  <Line type="monotone" dataKey="avg" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Reports() {
  const [period, setPeriod] = useState<Period>("month");
  const { store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const reportParams = { period, branchId: activeBranchId ?? undefined };
  const branchParams = { branchId: activeBranchId ?? undefined };
  const { data: pl, isLoading: loadingPl } = useGetProfitLoss(reportParams, { query: { queryKey: getGetProfitLossQueryKey(reportParams) } });
  const { data: expBreakdown, isLoading: loadingExp } = useGetExpenseBreakdown(branchParams);
  const { data: salesChart } = useGetSalesChart(branchParams);
  const isEnterprise = store?.plan === "enterprise";
  const isPro = store?.plan === "pro" || isEnterprise;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports — {activeBranch?.name ?? "All Branches"}</h1>
        <p className="text-muted-foreground mt-1">P&L, daily close, and expense analysis for the selected location.</p>
      </div>
      <Tabs defaultValue="pl">
        <TabsList>
          <TabsTrigger value="pl" className="gap-1.5"><TrendingUp className="w-3.5 h-3.5" />Profit & Loss</TabsTrigger>
          <TabsTrigger value="daily" className="gap-1.5"><Calendar className="w-3.5 h-3.5" />Daily Close</TabsTrigger>
          <TabsTrigger value="advanced" className="gap-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-500" />Advanced Analytics
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pl" className="mt-6">
          <PLContent period={period} setPeriod={setPeriod} pl={pl} loadingPl={loadingPl} expBreakdown={expBreakdown} loadingExp={loadingExp} salesChart={salesChart} isEnterprise={isEnterprise} isPro={isPro} />
        </TabsContent>
        <TabsContent value="daily" className="mt-6">
          <DailyCloseReport />
        </TabsContent>
        <TabsContent value="advanced" className="mt-6">
          <EnterpriseGate feature="Advanced Analytics" description="Get deep insights into your busiest hours, payment trends, and top-performing products. Upgrade to Enterprise to unlock this.">
            <AdvancedAnalytics />
          </EnterpriseGate>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PLContent({ period, setPeriod, pl, loadingPl, expBreakdown, loadingExp, salesChart, isEnterprise, isPro }: {
  period: Period; setPeriod: (p: Period) => void; pl: any; loadingPl: boolean;
  expBreakdown: any; loadingExp: boolean; salesChart: any; isEnterprise: boolean; isPro: boolean;
}) {
  const { store } = useAuth();

  const plChartData = pl ? [
    { name: "Revenue", value: pl.revenue, fill: 'hsl(var(--chart-1))' },
    { name: "Cost of Goods", value: pl.costOfGoods, fill: 'hsl(var(--chart-2))' },
    { name: "Gross Profit", value: pl.grossProfit, fill: 'hsl(var(--chart-3))' },
    { name: "Expenses", value: pl.expenses, fill: 'hsl(var(--chart-4))' },
    { name: "Net Profit", value: pl.netProfit, fill: pl.netProfit >= 0 ? 'hsl(var(--chart-1))' : 'hsl(var(--destructive))' },
  ] : [];

  const exportExcel = () => {
    if (!pl) return;
    const wb = XLSX.utils.book_new();

    const plData = [
      ["Metric", "Value"],
      ["Period", pl.period],
      ["Revenue", pl.revenue],
      ["Cost of Goods Sold", pl.costOfGoods],
      ["Gross Profit", pl.grossProfit],
      ["Gross Margin", `${pl.grossMargin}%`],
      ["Operating Expenses", pl.expenses],
      ["Net Profit", pl.netProfit],
      ["Net Margin", `${pl.netMargin}%`],
    ];
    const wspl = XLSX.utils.aoa_to_sheet(plData);
    XLSX.utils.book_append_sheet(wb, wspl, "P&L Summary");

    if (expBreakdown?.length) {
      const expData = [["Category", "Total"], ...expBreakdown.map((e: any) => [e.category, e.total])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(expData), "Expense Breakdown");
    }

    if (salesChart?.length) {
      const saleData = [["Date", "Revenue"], ...(salesChart as any[]).map((s: any) => [s.date, s.revenue])];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(saleData), "Sales Trend");
    }

    XLSX.writeFile(wb, `RetailPulse_Report_${period}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const exportPDF = () => {
    if (!pl) return;
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text("RetailPulse — Financial Report", 14, 20);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Period: ${pl.period}   Generated: ${new Date().toLocaleDateString("en-NG")}`, 14, 28);

    autoTable(doc, {
      startY: 36,
      head: [["Metric", "Value"]],
      body: [
        ["Revenue", formatCurrency(pl.revenue)],
        ["Cost of Goods Sold", formatCurrency(pl.costOfGoods)],
        ["Gross Profit", formatCurrency(pl.grossProfit)],
        ["Gross Margin", `${pl.grossMargin}%`],
        ["Operating Expenses", formatCurrency(pl.expenses)],
        ["Net Profit / (Loss)", formatCurrency(pl.netProfit)],
        ["Net Margin", `${pl.netMargin}%`],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [99, 102, 241] },
    });

    if (expBreakdown?.length) {
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 16,
        head: [["Expense Category", "Total"]],
        body: expBreakdown.map((e: any) => [e.category, formatCurrency(e.total)]),
        styles: { fontSize: 10 },
        headStyles: { fillColor: [99, 102, 241] },
      });
    }

    doc.save(`RetailPulse_Report_${period}_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold">Profit & Loss</h2>
          <p className="text-muted-foreground mt-1">Financial analysis for the selected period.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          {isPro ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1" onClick={exportExcel} disabled={!pl}>
                <FileSpreadsheet className="w-4 h-4" /> Excel
              </Button>
              <Button variant="outline" size="sm" className="gap-1" onClick={exportPDF} disabled={!pl}>
                <Download className="w-4 h-4" /> PDF
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="gap-1 opacity-60 cursor-not-allowed" disabled title="Pro plan required to export">
              <Crown className="w-4 h-4 text-amber-500" /> Export
            </Button>
          )}
        </div>
      </div>

      {loadingPl ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : pl ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                <DollarSign className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(pl.revenue)}</div>
                <p className="text-xs text-muted-foreground">{pl.period}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Gross Profit</CardTitle>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(pl.grossProfit)}</div>
                <p className="text-xs text-muted-foreground">Margin: {pl.grossMargin}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
                {pl.netProfit >= 0 ? <TrendingUp className="w-4 h-4 text-green-500" /> : <TrendingDown className="w-4 h-4 text-destructive" />}
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${pl.netProfit < 0 ? "text-destructive" : ""}`}>{formatCurrency(pl.netProfit)}</div>
                <p className="text-xs text-muted-foreground">Net margin: {pl.netMargin}%</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle>Profit & Loss Overview</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={plChartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                      <YAxis fontSize={11} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                      <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: '8px' }} formatter={(v: number) => [formatCurrency(v), ""]} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {plChartData.map((entry, index) => <Cell key={index} fill={entry.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Expense Breakdown</CardTitle></CardHeader>
              <CardContent>
                {loadingExp ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={expBreakdown} cx="50%" cy="45%" outerRadius={80} dataKey="total" nameKey="category">
                          {expBreakdown?.map((_: unknown, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Legend formatter={(v) => <span className="text-xs">{v}</span>} />
                        <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: '8px' }} formatter={(v: number) => [formatCurrency(v), ""]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sales trend */}
          {salesChart && (salesChart as any[]).length > 0 && (
            <Card>
              <CardHeader><CardTitle>Sales Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={salesChart as any[]} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
                      <YAxis fontSize={10} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                      <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: '8px' }} formatter={(v: number) => [formatCurrency(v), "Revenue"]} />
                      <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>P&L Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 max-w-sm">
                <div className="flex justify-between text-sm py-2 border-b">
                  <span>Revenue</span><span className="font-semibold">{formatCurrency(pl.revenue)}</span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b text-muted-foreground">
                  <span>Cost of Goods Sold</span><span>-{formatCurrency(pl.costOfGoods)}</span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b font-medium">
                  <span>Gross Profit</span><span>{formatCurrency(pl.grossProfit)} ({pl.grossMargin}%)</span>
                </div>
                <div className="flex justify-between text-sm py-2 border-b text-muted-foreground">
                  <span>Operating Expenses</span><span>-{formatCurrency(pl.expenses)}</span>
                </div>
                <div className={`flex justify-between text-sm py-2 font-bold text-base ${pl.netProfit < 0 ? "text-destructive" : "text-primary"}`}>
                  <span>Net Profit</span><span>{formatCurrency(pl.netProfit)} ({pl.netMargin}%)</span>
                </div>
              </div>
            </CardContent>
          </Card>

        </>
      ) : null}
    </div>
  );
}
